import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Sparkles, ArrowRight, Bot, BookOpen, Layers, Users, CheckCircle2,
  MessageSquare, Building2, Zap, Shield, ChevronDown, Menu, X,
  FileText, ArrowUpRight, Award, Lock, HelpCircle
} from 'lucide-react';
import './LandingPage.css';

const LandingPage = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('onboarding');
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = 'MentorAI - Intelligent Employee Onboarding & AI Mentoring';
  }, []);

  const handleLoginClick = () => {
    if (user) {
      navigate(isSuperAdmin ? '/admin' : '/dashboard');
    } else {
      navigate('/login');
    }
  };

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const faqs = [
    {
      q: 'What is MentorAI and how does it assist new joinees?',
      a: 'MentorAI is an enterprise onboarding and mentoring portal that pairs every employee with AI-driven mentors. From company policy exploration using Retrieval-Augmented Generation (RAG) to department-specific assessments and project architecture roadmaps, MentorAI automates the entire employee enablement lifecycle.'
    },
    {
      q: 'How does Policy Chat guarantee 100% accurate company information?',
      a: 'Policy Chat utilizes context-bounded RAG architecture. When you query policies, leave structures, or company regulations, the AI searches only official, admin-indexed company documentation and cites exact answers, eliminating hallucinations.'
    },
    {
      q: 'Can departments customize their own AI leads and assessments?',
      a: 'Yes! Super Admins can configure custom department personas with custom AI lead avatars, specific cover banners, and department-focused profiling questions that guide employees through their specific tools and tech stacks.'
    },
    {
      q: 'How are employee project assignments kept secure?',
      a: 'MentorAI enforces strict role-based access control. Joinees can only query and access AI mentoring for projects to which they are explicitly assigned by administrative leads.'
    }
  ];

  return (
    <div className="landing-root">
      {/* Background Ambient Glow Orbs */}
      <div className="ambient-glow glow-1" />
      <div className="ambient-glow glow-2" />
      <div className="ambient-glow glow-3" />

      {/* Navigation Bar */}
      <header className="landing-navbar">
        <div className="landing-nav-container">
          <div className="landing-brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="brand-logo-glow">
              <Sparkles size={22} className="brand-icon" />
            </div>
            <span className="brand-name">
              Mentor<span className="brand-highlight">AI</span>
            </span>
          </div>

          <nav className={`landing-nav-links ${mobileMenuOpen ? 'nav-open' : ''}`}>
            <button className="nav-link-btn" onClick={() => scrollToSection('features')}>Features</button>
            <button className="nav-link-btn" onClick={() => scrollToSection('how-it-works')}>Workflow</button>
            <button className="nav-link-btn" onClick={() => scrollToSection('ai-leads')}>AI Mentors</button>
            <button className="nav-link-btn" onClick={() => scrollToSection('security')}>Security</button>
            <button className="nav-link-btn" onClick={() => scrollToSection('faq')}>FAQ</button>
          </nav>

          <div className="landing-nav-actions">
            <button
              id="landing-login-btn"
              className="btn btn-primary nav-login-btn"
              onClick={handleLoginClick}
            >
              <span>{user ? 'Enter Portal' : 'Login'}</span>
              <ArrowRight size={16} />
            </button>

            <button
              className="mobile-toggle-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-badge">
          <span className="pulse-indicator"></span>
          <Sparkles size={14} />
          <span>Next-Gen Enterprise Onboarding & Mentoring Platform</span>
        </div>

        <h1 className="hero-title">
          Empower New Joinees with <br />
          <span className="gradient-text">Context-Bounded AI Mentorship</span>
        </h1>

        <p className="hero-subtitle">
          Accelerate employee ramp-up with instant self-service policy inquiries,
          interactive department profiling, curated project workflows, and 24/7 personalized AI team leads.
        </p>

        <div className="hero-cta-group">
          <button id="hero-get-started-btn" className="btn btn-primary hero-btn-main" onClick={handleLoginClick}>
            <span>{user ? 'Go to Your Dashboard' : 'Access Employee Portal'}</span>
            <ArrowRight size={18} />
          </button>
          <button className="btn btn-secondary hero-btn-secondary" onClick={() => scrollToSection('how-it-works')}>
            <BookOpen size={18} />
            <span>Discover How It Works</span>
          </button>
        </div>

        {/* Floating Mockup / Live Showcase Window */}
        <div className="hero-showcase-container">
          <div className="showcase-window">
            <div className="window-header">
              <div className="window-dots">
                <span className="dot dot-red"></span>
                <span className="dot dot-yellow"></span>
                <span className="dot dot-green"></span>
              </div>
              <div className="window-title">
                <Bot size={14} /> MentorAI Policy Intelligence & Joinee Terminal
              </div>
              <div className="window-status">
                <span className="status-live-dot"></span> Online RAG Active
              </div>
            </div>

            <div className="window-body">
              <div className="chat-simulation">
                <div className="chat-bubble user-bubble">
                  <div className="bubble-avatar user-avatar">You</div>
                  <div className="bubble-content">
                    <p>What is our annual leave policy and how do I request time off during my probation period?</p>
                  </div>
                </div>

                <div className="chat-bubble ai-bubble">
                  <div className="bubble-avatar ai-avatar">
                    <Sparkles size={16} />
                  </div>
                  <div className="bubble-content">
                    <div className="ai-tag"><Building2 size={12} /> Company HR Policy Document • Section 4.2</div>
                    <p>
                      Employees accrue <strong>1.5 days per month</strong> (18 days annually). During your initial 3-month probation period, personal leave can be approved with formal email consent from your designated Department Lead and HR Officer.
                    </p>
                    <div className="ai-citation">
                      <FileText size={13} /> Source: Verified Employee Handbook 2026.pdf (Matched with 99.4% confidence)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating Metric Badges */}
          <div className="floating-chip chip-left">
            <Award size={20} className="chip-icon" />
            <div>
              <strong>4x Faster Ramp-Up</strong>
              <span>Instant policy clarity</span>
            </div>
          </div>

          <div className="floating-chip chip-right">
            <Shield size={20} className="chip-icon" />
            <div>
              <strong>100% Verified Answers</strong>
              <span>Zero LLM Hallucinations</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="stats-bar-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">99.8%</div>
            <div className="stat-label">Verified Policy Accuracy</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">&lt; 2s</div>
            <div className="stat-label">AI Response Latency</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">100%</div>
            <div className="stat-label">Role-Based Data Privacy</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">24/7</div>
            <div className="stat-label">Always-On Mentor Availability</div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section id="features" className="features-section">
        <div className="section-header">
          <div className="section-pill"><Zap size={14} /> Core Capabilities</div>
          <h2 className="section-title">Engineered for Rapid, Confident Onboarding</h2>
          <p className="section-subtitle">
            Say goodbye to endless handbook skimming and repetitive HR emails. MentorAI centralizes all knowledge into intelligent, proactive agents.
          </p>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon-box icon-purple">
              <MessageSquare size={26} />
            </div>
            <h3 className="feature-title">RAG Policy Intelligence</h3>
            <p className="feature-desc">
              Context-bounded query engine reads your approved company policies, FAQs, and employment documents to deliver trustworthy, instant answers.
            </p>
            <div className="feature-perk">
              <CheckCircle2 size={16} /> Direct Document Citations
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon-box icon-indigo">
              <Bot size={26} />
            </div>
            <h3 className="feature-title">Department AI Leads</h3>
            <p className="feature-desc">
              Each division (Engineering, Design, Legal, HR) features a dedicated AI Lead persona with custom avatars, greetings, and tailored domain expertise.
            </p>
            <div className="feature-perk">
              <CheckCircle2 size={16} /> Personalized Department Mentors
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon-box icon-emerald">
              <Layers size={26} />
            </div>
            <h3 className="feature-title">Project Architecture Hub</h3>
            <p className="feature-desc">
              Access workflow roadmaps, coding conventions, repository documentation, and test criteria for every project assigned to your roster profile.
            </p>
            <div className="feature-perk">
              <CheckCircle2 size={16} /> Secure Project Segregation
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon-box icon-blue">
              <Users size={26} />
            </div>
            <h3 className="feature-title">Interactive Joinee Profiling</h3>
            <p className="feature-desc">
              Multi-step sequential onboarding wizard captures joinee background, past experience, and potential obstacles to tailor their mentoring journey.
            </p>
            <div className="feature-perk">
              <CheckCircle2 size={16} /> Automated Progress Tracking
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon-box icon-amber">
              <Building2 size={26} />
            </div>
            <h3 className="feature-title">Active HR Persona Control</h3>
            <p className="feature-desc">
              Super Admins configure and activate corporate HR personas complete with real executive avatars, custom welcoming banners, and global greetings.
            </p>
            <div className="feature-perk">
              <CheckCircle2 size={16} /> Centralized Persona Management
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon-box icon-rose">
              <Shield size={26} />
            </div>
            <h3 className="feature-title">Enterprise Security & Auth</h3>
            <p className="feature-desc">
              JWT authentication, encrypted credential storage, role-enforced API gateways, and strict tenant isolation protect sensitive corporate intellectual property.
            </p>
            <div className="feature-perk">
              <CheckCircle2 size={16} /> Bank-Grade Access Security
            </div>
          </div>
        </div>
      </section>

      {/* Interactive "How It Works" Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="section-header">
          <div className="section-pill"><BookOpen size={14} /> Interactive Workflow</div>
          <h2 className="section-title">From First Day to Confident Contributor</h2>
          <p className="section-subtitle">
            Experience how MentorAI guides every new hire seamlessly from day one.
          </p>
        </div>

        <div className="interactive-tabs-container">
          <div className="tabs-nav">
            <button
              className={`tab-btn ${activeTab === 'onboarding' ? 'active' : ''}`}
              onClick={() => setActiveTab('onboarding')}
            >
              <span className="tab-num">01</span>
              <span>Joinee Setup & Profile</span>
            </button>

            <button
              className={`tab-btn ${activeTab === 'policies' ? 'active' : ''}`}
              onClick={() => setActiveTab('policies')}
            >
              <span className="tab-num">02</span>
              <span>Policy Intelligence</span>
            </button>

            <button
              className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
              onClick={() => setActiveTab('projects')}
            >
              <span className="tab-num">03</span>
              <span>Assigned Projects</span>
            </button>
          </div>

          <div className="tab-content-card">
            {activeTab === 'onboarding' && (
              <div className="tab-grid">
                <div className="tab-text">
                  <h3>Seamless 3-Stage Orientation Wizard</h3>
                  <p>
                    New joinees begin with an interactive assessment that collects their background, technical comfort level, and departmental readiness.
                  </p>
                  <ul className="tab-bullet-list">
                    <li><CheckCircle2 size={16} /> Automated welcome greeting from the active HR Persona</li>
                    <li><CheckCircle2 size={16} /> Predefined company orientation questionnaires</li>
                    <li><CheckCircle2 size={16} /> Department-specific skill and background surveys</li>
                  </ul>
                </div>
                <div className="tab-preview">
                  <div className="preview-card-mockup">
                    <div className="mockup-header">
                      <Sparkles size={16} className="text-primary" />
                      <span>Onboarding Hub • Progress 100%</span>
                    </div>
                    <div className="mockup-body">
                      <div className="mockup-step-item done">
                        <CheckCircle2 size={16} /> General Company Orientation
                      </div>
                      <div className="mockup-step-item done">
                        <CheckCircle2 size={16} /> Meet Your Department Lead
                      </div>
                      <div className="mockup-step-item active">
                        <ArrowRight size={16} /> Department Skill Readiness
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'policies' && (
              <div className="tab-grid">
                <div className="tab-text">
                  <h3>Ask Anything, Anytime with Citation Proof</h3>
                  <p>
                    No more waiting days for HR replies on standard benefits, health insurance, leave allowances, or remote work protocols.
                  </p>
                  <ul className="tab-bullet-list">
                    <li><CheckCircle2 size={16} /> Natural language Q&A backed by verified company handbooks</li>
                    <li><CheckCircle2 size={16} /> Direct line-level citation transparency</li>
                    <li><CheckCircle2 size={16} /> Frequent FAQ suggestions for quick single-click answers</li>
                  </ul>
                </div>
                <div className="tab-preview">
                  <div className="preview-card-mockup">
                    <div className="mockup-header">
                      <Bot size={16} className="text-secondary" />
                      <span>Policy Assistant Q&A</span>
                    </div>
                    <div className="mockup-chat-snippet">
                      <div className="mini-user-q">"What is the work-from-home reimbursement allowance?"</div>
                      <div className="mini-ai-a">
                        Under our 2026 Remote Policy, full-time employees are eligible for up to <strong>$500 annually</strong> for ergonomic home office accessories.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'projects' && (
              <div className="tab-grid">
                <div className="tab-text">
                  <h3>Direct Access to Assigned Repositories & Workflows</h3>
                  <p>
                    Joinees hit the ground running with architectural breakdowns and step-by-step instructions for the exact repositories they are assigned to.
                  </p>
                  <ul className="tab-bullet-list">
                    <li><CheckCircle2 size={16} /> Project workflow and deployment procedures</li>
                    <li><CheckCircle2 size={16} /> Architecture pattern overviews</li>
                    <li><CheckCircle2 size={16} /> Context-bounded Q&A tailored solely to the project codebase</li>
                  </ul>
                </div>
                <div className="tab-preview">
                  <div className="preview-card-mockup">
                    <div className="mockup-header">
                      <Layers size={16} className="text-accent" />
                      <span>Assigned Project • Core API Service</span>
                    </div>
                    <div className="mockup-body">
                      <div className="mockup-project-tag">CI/CD Pipeline: GitHub Actions • Docker</div>
                      <div className="mockup-project-desc">
                        Standard microservices architecture using Django REST framework and PostgreSQL database replication.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* AI Mentors Section */}
      <section id="ai-leads" className="ai-leads-section">
        <div className="section-header">
          <div className="section-pill"><Bot size={14} /> Persona Ecosystem</div>
          <h2 className="section-title">Specialized Mentors for Every Department</h2>
          <p className="section-subtitle">
            Every division is accompanied by an AI mentor tuned to the exact vocabulary, tools, and objectives of that department.
          </p>
        </div>

        <div className="mentors-grid">
          <div className="mentor-card">
            <div className="mentor-avatar-badge" style={{ borderColor: '#6366f1' }}>
              <Bot size={36} style={{ color: '#6366f1' }} />
            </div>
            <h4>Engineering AI Lead</h4>
            <span className="mentor-dept-tag">Development & QA</span>
            <p>Assists with git branching strategies, API contracts, test coverage conventions, and codebase reviews.</p>
          </div>

          <div className="mentor-card">
            <div className="mentor-avatar-badge" style={{ borderColor: '#10b981' }}>
              <Users size={36} style={{ color: '#10b981' }} />
            </div>
            <h4>HR & People AI Officer</h4>
            <span className="mentor-dept-tag">Human Resources</span>
            <p>Welcomes joinees, explains leave policies, medical insurance coverage, and team culture guidelines.</p>
          </div>

          <div className="mentor-card">
            <div className="mentor-avatar-badge" style={{ borderColor: '#0ea5e9' }}>
              <Layers size={36} style={{ color: '#0ea5e9' }} />
            </div>
            <h4>Product & Design AI Lead</h4>
            <span className="mentor-dept-tag">Design & UX</span>
            <p>Guides joinees through Figma libraries, brand color tokens, user research repositories, and sprint planning.</p>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="security-section">
        <div className="security-card">
          <div className="security-icon-circle">
            <Lock size={32} />
          </div>
          <div className="security-text">
            <h3>Enterprise-Grade Privacy & Access Isolation</h3>
            <p>
              Your company handbooks, proprietary workflow diagrams, and joinee responses are strictly kept private.
              MentorAI guarantees strict tenant segregation, zero data leakage into public LLM training sets, and authenticated JWT session management.
            </p>
          </div>
          <div className="security-badges">
            <div className="sec-badge"><Shield size={16} /> Role-Based Access</div>
            <div className="sec-badge"><Lock size={16} /> AES-256 Encryption</div>
            <div className="sec-badge"><CheckCircle2 size={16} /> Private RAG Store</div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="section-header">
          <div className="section-pill"><HelpCircle size={14} /> Frequently Asked Questions</div>
          <h2 className="section-title">Everything You Need to Know</h2>
        </div>

        <div className="faq-container">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className={`faq-item ${openFaq === idx ? 'open' : ''}`}
              onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
            >
              <div className="faq-question">
                <span>{faq.q}</span>
                <ChevronDown size={20} className="faq-chevron" />
              </div>
              {openFaq === idx && (
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Bottom Banner */}
      <section className="bottom-cta-section">
        <div className="bottom-cta-card">
          <h2>Ready to Transform Your Employee Onboarding?</h2>
          <p>
            Experience next-level workforce acceleration with MentorAI. Sign in with your credentials to access your dashboard.
          </p>
          <button id="bottom-cta-btn" className="btn btn-primary cta-btn-large" onClick={handleLoginClick}>
            <span>{user ? 'Open Dashboard' : 'Sign In to MentorAI'}</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Modern Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-col brand-col">
            <div className="footer-brand">
              <Sparkles size={20} className="brand-icon" />
              <span>Mentor<span className="brand-highlight">AI</span></span>
            </div>
            <p className="footer-tagline">
              Autonomous Employee Onboarding & Context-Bounded AI Mentorship System.
            </p>
            <div className="footer-copyright">
              © {new Date().getFullYear()} Technaureus Info Solutions. All rights reserved.
            </div>
          </div>

          <div className="footer-col">
            <h4>Platform</h4>
            <a href="#features" onClick={() => scrollToSection('features')}>Policy Chat (RAG)</a>
            <a href="#how-it-works" onClick={() => scrollToSection('how-it-works')}>Onboarding Wizard</a>
            <a href="#ai-leads" onClick={() => scrollToSection('ai-leads')}>Department AI Leads</a>
            <a href="#security" onClick={() => scrollToSection('security')}>Security & Privacy</a>
          </div>

          <div className="footer-col">
            <h4>Access</h4>
            <button className="footer-link-btn" onClick={handleLoginClick}>Employee Login</button>
            <button className="footer-link-btn" onClick={handleLoginClick}>Super Admin Portal</button>
            <a href="#faq" onClick={() => scrollToSection('faq')}>Help & FAQ</a>
          </div>

          <div className="footer-col">
            <h4>Technology</h4>
            <span className="tech-pill">Django REST Framework</span>
            <span className="tech-pill">React 18 + SPA</span>
            <span className="tech-pill">Groq LLM Engine</span>
            <span className="tech-pill">RAG Vector Indexing</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
